import React, { useCallback, useState } from 'react'
import { Segment, Header, Container, Grid, Form, Button, Icon, FormTextArea, FormField, ButtonGroup, Placeholder } from 'semantic-ui-react'
import DataTable from '../../components/data_table'
import { append, dateToString, handleRedirect, isObject, NotFoundError, preventDefault } from '../../helpers'
import { getTaskExecution, getTaskExecutionLogEntries, retryTaskExecution, updateTaskExecution } from '../../api/tasks'
import { Await, defer, useLoaderData, useNavigate, useRouteLoaderData } from 'react-router-dom'
import { preloadedEntity, useMessages } from '../root'
import { Response } from '../../api'

export async function loader({ request: { signal }, params: { executionId } }) {
	if (isObject(preloadedEntity.current) && typeof preloadedEntity.current.id === 'number' && preloadedEntity.current.id.toString() === executionId) {
		const execution = new Response({data: preloadedEntity.current})
		preloadedEntity.current = null
		return { executionId, execution }
	}
	const execution = getTaskExecution(executionId, { signal }).catch(e => {
		if (isObject(e) && isObject(e.res) && typeof e.res.redirect === 'string') {
			return null
		} else if (isObject(e) && isObject(e.res) && isObject(e.res.error) && e.res.error.code === 'not_found') {
			return Promise.reject(new NotFoundError('Fant ikke kjøringen: ' + e.message))
		} else {
			return Promise.reject(new Error('Kunne ikke laste inn kjøringen: ' + e.message))
		}
	})
	return defer({ executionId, execution })
}

const initialUpdatedExecutionData = {}

export default function TaskExecutionPage() {
	const { tasks } = useRouteLoaderData('tasks')
	const { executionId, execution: initialExecution } = useLoaderData()
	const [updatedExecutionData, setUpdatedExecutionData] = useState(initialUpdatedExecutionData)
	const [, setMessages] = useMessages()
	const [inputChange, setInputChange] = useState(null)
	const [outputChange, setOutputChange] = useState(null)
	const [updatingInput, setUpdatingInput] = useState(false)
	const [updatingOutput, setUpdatingOutput] = useState(false)
	const [retryTaskState, setRetryTaskState] = useState(null)
	const routerNavigateFn = useNavigate()

	const triggerRetry = (task_state = null) => retryTaskExecution(executionId, {task_state}).then(res => {
		setMessages(messages => messages.concat({
			key: 'task_retry_success_' + Math.round(new Date() / 1000).toString(),
			dismissable: true,
			type: 'success',
			icon: 'check',
			content: 'Oppgaven er lagt i kø for å prøve igjen',
		}))
		handleRedirect(res, routerNavigateFn)
	}).catch(e => {
		setMessages(messages => messages.concat({
			key: 'task_retry_error_' + Math.round(new Date() / 1000).toString(),
			dismissable: true,
			type: 'error',
			icon: 'exclamation circle',
			title: 'En feil oppstod ved forsøk på å prøve oppgaven igjen',
			content: e.message
		}))
		handleRedirect(e.res, routerNavigateFn)
	})

	const apiSearch = useCallback(async (query, options, requestOptions) => {
		const res = await getTaskExecutionLogEntries(executionId, query, options, requestOptions)
		handleRedirect(res, routerNavigateFn)
		return res
	}, [executionId, routerNavigateFn])

	return <>
		<Segment vertical padded="very">
			<Container>
				<Header size="huge">
					Kjøring #{executionId} av <React.Suspense fallback={<Placeholder><Placeholder.Line /></Placeholder>}>
						<Await resolve={initialExecution}>
							{initialExecution => {
								const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
								return execution.task_display_name
							}}
						</Await>
					</React.Suspense>
					<Header.Subheader>Viser status, jobb-status, input og output i JSON-format, samt. logger for kjøring av bakgrunnsjobben.</Header.Subheader>
				</Header>

				<Grid>
					<Grid.Row columns={2}>
						<Grid.Column>
							<Header>Status</Header>
							<React.Suspense fallback={<Placeholder><Placeholder.Line /></Placeholder>}>
								<Await resolve={initialExecution}>
									{initialExecution => {
										const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
										return <p>{execution.state_display_name} ({execution.state_updated_at !== null ? dateToString(execution.state_updated_at, true, true) : 'Ikke startet enda'})</p>
									}}
								</Await>
							</React.Suspense>
							<React.Suspense>
								<Await resolve={Promise.all([tasks, initialExecution])}>
									{([tasks, initialExecution]) => {
										const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
										if (!execution.can_retry) return null

										const taskStates = (isObject(tasks) && isObject(tasks.data) && isObject(tasks.data.tasks) && tasks.data.tasks.hasOwnProperty(execution.task)) && isObject(tasks.data.tasks[execution.task].task_states) ? tasks.data.tasks[execution.task].task_states : {}
										const taskStateOptions = Object.keys(taskStates).map(value => ({
											value: taskStates[value].state,
											text: taskStates[value].name !== null ? taskStates[value].name : 'Ingen jobb-status valgt',
										}))

										const retryButton = <Button onClick={preventDefault(() => triggerRetry(taskStateOptions.length > 1 ? ((retryTaskState === null ? execution.task_state : retryTaskState) ?? 0) : null))}>
											<Icon name="refresh" /> Prøv igjen
										</Button>

										if (taskStateOptions.length > 1) {
											return <Form as="div">
												<Form.Group>
													<Form.Dropdown
														selection
														options={taskStateOptions}
														value={(retryTaskState === null ? execution.task_state : retryTaskState) ?? 0}
														onChange={(_, data) => setRetryTaskState(data.value)}
													/>
													<Form.Field>
														{retryButton}
													</Form.Field>
												</Form.Group>
											</Form>
										}

										return retryButton
									}}
								</Await>
							</React.Suspense>
						</Grid.Column>
						<Grid.Column>
							<Header>Jobb-status</Header>
							<React.Suspense fallback={<Placeholder><Placeholder.Line /></Placeholder>}>
								<Await resolve={initialExecution}>
									{initialExecution => {
										const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
										return <p>{execution.task_state_display_name}</p>
									}}
								</Await>
							</React.Suspense>
						</Grid.Column>
					</Grid.Row>
				</Grid>
				<p></p>

				<Grid>
					<Grid.Row columns={2}>
						<Grid.Column>
							<Header>Input</Header>
							<Form>
								<React.Suspense fallback={<FormTextArea disabled />}>
									<Await resolve={initialExecution}>
										{initialExecution => {
											const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
											return <FormTextArea
												value={inputChange ?? JSON.stringify(execution.input)}
												onChange={(e, data) => setInputChange(data.value === execution.input ? null : data.value)}
											/>
										}}
									</Await>
								</React.Suspense>
								<FormField>
									<ButtonGroup>
										{inputChange === null ? null : <Button
											color="orange"
											loading={updatingInput}
											disabled={updatingInput}
											onClick={preventDefault(() => {
												setUpdatingInput(true)
												updateTaskExecution(executionId, { input: inputChange })
													.then(res => {
														setInputChange(null)
														setUpdatingInput(false)
														setUpdatedExecutionData(execution => append(execution, { input: res.data.input }))
														handleRedirect(res, routerNavigateFn)
													})
													.catch(e => {
														setUpdatingInput(false)
														setMessages(messages => messages.concat({
															key: 'task_execution_update_error_' + Math.round(new Date() / 1000).toString(),
															dismissable: true,
															type: 'error',
															icon: 'exclamation circle',
															title: 'En feil oppstod',
															content: 'Kunne ikke oppdatere bakgrunnsjobben: ' + e.message
														}))
														handleRedirect(e.res, routerNavigateFn)
													})
											})}
										>Lagre endringer</Button>}
										{inputChange === null ? null : <Button
											loading={updatingInput}
											disabled={updatingInput}
											onClick={preventDefault(() => setInputChange(null))}
										>Tilbakestill endringer</Button>}
									</ButtonGroup>
								</FormField>
							</Form>
						</Grid.Column>
						<Grid.Column>
							<Header>Output</Header>
							<Form>
								<React.Suspense fallback={<FormTextArea disabled />}>
									<Await resolve={initialExecution}>
										{initialExecution => {
											const execution = append(isObject(initialExecution) && isObject(initialExecution.data) ? initialExecution.data : {}, updatedExecutionData)
											return <FormTextArea
												value={outputChange ?? JSON.stringify(execution.output)}
												onChange={(e, data) => setOutputChange(data.value === execution.output ? null : data.value)}
											/>
										}}
									</Await>
								</React.Suspense>
								<FormField>
									<ButtonGroup>
										{outputChange === null ? null : <Button
											color="orange"
											loading={updatingOutput}
											disabled={updatingOutput}
											onClick={preventDefault(() => {
												setUpdatingOutput(true)
												updateTaskExecution(executionId, { output: outputChange })
													.then(res => {
														setOutputChange(null)
														setUpdatingOutput(false)
														setUpdatedExecutionData(execution => append(execution, { output: res.data.output }))
													})
													.catch(e => {
														setUpdatingOutput(false)
														setMessages(messages => messages.concat({
															key: 'task_execution_update_error_' + Math.round(new Date() / 1000).toString(),
															dismissable: true,
															type: 'error',
															icon: 'exclamation circle',
															title: 'En feil oppstod',
															content: 'Kunne ikke oppdatere bakgrunnsjobben: ' + e.message
														}))
													})
											})}
										>Lagre endringer</Button>}
										{outputChange === null ? null : <Button
											loading={updatingOutput}
											disabled={updatingOutput}
											onClick={preventDefault(() => setOutputChange(null))}
										>Tilbakestill endringer</Button>}
									</ButtonGroup>
								</FormField>
							</Form>
						</Grid.Column>
					</Grid.Row>
				</Grid>
				<p></p>

				<Header>Logg</Header>
				<DataTable
					className="kit-log-table"
					columns={[
						{ key: 'created_at', text: 'Tidspunkt', visible: true, sortable: true },
						{ key: 'severity', text: 'Type', visible: true, sortable: true },
						{ key: 'entry', text: 'Logg', visible: true, sortable: true, searchable: true },
					]}
					defaultOrder={{'created_at': 'DESC'}}
					apiSearch={apiSearch}
					renderRow={data => [
						dateToString(data.created_at, true, true),
						data.display_severity,
						<pre>{data.entry}</pre>,
					]}
				/>
			</Container>
		</Segment>
	</>
}